import React, { useState, memo } from 'react';
import { STATUS_CATEGORIES } from '../../../FlowchartView/TaskNode/constants';
import ColumnHeader from './ColumnHeader';
import ColumnCards from './ColumnCards';
import CollapsedContent from './CollapsedContent';
import DeleteColumnModal from '../DeleteColumnModal';

/**
 * KanbanColumn Component
 * A single column representing a status, with dnd-kit sortable cards
 */
const KanbanColumn = memo(({
  column,
  tasks,
  taskIds,
  categories,
  allTags = [],
  allTasks = [],
  nestedSprints,
  selectedTaskIds,
  isCollapsed,
  isExpanding,
  isEditingName,
  activeCardId,
  onToggleCollapse,
  onTaskClick,
  onOpenDetails,
  onOpenAttachments,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onIconChange,
  onRename,
  onStatusChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onDeleteTask,
  onDuplicateTask,
  onCopyCard,
  onDuplicateCard,
  onCreateParallelMission,
  onInsertMissionBefore,
  onSetStatusAutoSync,
  onDeliverablesChange,
  onInputsChange,
  onArchiveTask,
  onUnarchiveTask,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  onAddTask,
  onAddDecision,
  onAddWait,
  onColumnNameSave,
  onDeleteColumn,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onReassignTasksStatus,
  onSetWipLimit,
  statuses,
  lockStates,
  cardDisplaySettings = {},
  // From SortableColumn wrapper
  dragListeners,
  isDraggingColumn,
  // Performance optimization during column/card drag
  isAnyColumnDragging = false,
  isAnyCardDragging = false, // PERFORMANCE: Virtualize cards during card drag too
  isDragOverlay = false,
  frozenTaskCount, // Frozen count during drag so numbers don't change
  frozenHiddenCount, // Frozen hidden count during drag so +X more doesn't change
  visibleCardCount = 8, // How many cards fit in viewport (calculated on drag start)
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
}) => {
  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  // Track which card should be in edit mode (for newly created cards)
  const [editingCardId, setEditingCardId] = useState(null);

  // Get category info for column styling
  const categoryInfo = STATUS_CATEGORIES[column.category];
  
  // Use frozen count during drag, otherwise use actual count
  const displayTaskCount = frozenTaskCount ?? tasks.length;

  const columnClasses = [
    'kanban-column',
    `kanban-column--${column.category}`,
    isCollapsed && 'kanban-column--collapsed',
    isExpanding && 'kanban-column--expanding',
    isDraggingColumn && 'kanban-column--dragging',
  ].filter(Boolean).join(' ');

  // Card event handlers object for ColumnCards
  const cardHandlers = {
    onTaskClick,
    onOpenDetails,
    onOpenAttachments,
    onCategoryChange,
    onRenameCategory,
    onDeleteCategory,
    onAddTag,
    onRemoveTag,
    onSetPrimaryTag,
    onRenameTag,
    onDeleteTag,
    onOpenNestedSprint,
    onCreateNestedSprint,
    onRemoveNestedSprint,
    onIconChange,
    onRename,
    onStatusChange,
    onDueDateChange,
    onRemindersChange,
    onEstimatedDurationMinutesChange,
    onCreatedAtChange,
    onStartedAtChange,
    onCompletedAtChange,
    onUpdatedAtChange,
    onPriorityChange,
    onDifficultyChange,
    onCustomFieldValueChange,
    customFields,
    onDeleteTask,
    onCopyCard,
    onDuplicateCard,
    onCreateParallelMission,
    onInsertMissionBefore,
    onSetStatusAutoSync,
    onDeliverablesChange,
    onInputsChange,
    onArchiveTask,
    onUnarchiveTask,
    onDecisionAnswerChange,
    onDecisionOptionsChange,
    onAddStatus,
    onDeleteStatus,
    onRenameStatus,
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView,
    onSubmitDeliverable,
    onEditDeliverables,
    onClearDeliverable,
    onNavigateToParentMission,
  };

  return (
    <div
      className={columnClasses}
      data-column-id={column.id}
      onClick={isCollapsed ? () => onToggleCollapse?.(column.id) : undefined}
      style={{
        '--column-color': column.color,
        '--category-accent': categoryInfo?.color || column.color,
      }}
    >
      {/* Collapsed content - visible when collapsed */}
      <CollapsedContent
        column={column}
        taskCount={displayTaskCount}
        dragListeners={dragListeners}
      />

      {/* Expanded content - visible when not collapsed */}
      <div className="kanban-column__expanded-content">
        <ColumnHeader
          column={column}
          taskCount={displayTaskCount}
          statuses={statuses}
          dragListeners={dragListeners}
          onColumnNameSave={onColumnNameSave}
          onToggleCollapse={onToggleCollapse}
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          onOpenDeleteModal={() => setIsDeleteModalOpen(true)}
          setEditingCardId={setEditingCardId}
          onSetWipLimit={onSetWipLimit}
        />
        
        <ColumnCards
          column={column}
          tasks={tasks}
          taskIds={taskIds}
          categories={categories}
          allTags={allTags}
          allTasks={allTasks}
          nestedSprints={nestedSprints}
          statuses={statuses}
          lockStates={lockStates}
          selectedTaskIds={selectedTaskIds}
          cardDisplaySettings={cardDisplaySettings}
          editingCardId={editingCardId}
          setEditingCardId={setEditingCardId}
          isAnyColumnDragging={isAnyColumnDragging}
          isAnyCardDragging={isAnyCardDragging}
          isDragOverlay={isDragOverlay}
          isCollapsed={isCollapsed}
          visibleCardCount={visibleCardCount}
          frozenHiddenCount={frozenHiddenCount}
          {...cardHandlers}
        />
      </div>

      {/* Delete Column Modal */}
      <DeleteColumnModal
        isOpen={isDeleteModalOpen}
        columnName={column.name}
        columnId={column.id}
        taskCount={tasks.length}
        statuses={statuses}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={({ targetStatusId, newStatus }) => {
          setIsDeleteModalOpen(false);
          
          if (newStatus) {
            onAddStatus?.(newStatus);
          }
          
          if (targetStatusId && tasks.length > 0) {
            onReassignTasksStatus?.(tasks.map(t => t.id), targetStatusId);
          }
          
          onDeleteColumn?.(column.id);
        }}
      />
    </div>
  );
});

export default KanbanColumn;
